import React, { useState, useEffect, useRef } from "react";
import "./Fellowships.css";
import { Afri, Epp, Fam, Mins, Nm, Singles } from "../../images/images";
import Button from "../button/Button";
import { fadeIn, staggerContainer } from "../../utils/motion";
import { motion } from "framer-motion";

const images = [
  { src: Fam, label: "Marriage - Family" },
  { src: Singles, label: "Singles" },
  { src: Afri, label: "Africa Prayer Furnace" },
  { src: Epp, label: "Europe prayer Furnace" },
  { src: Nm, label: "North America Prayer Conference" },
  { src: Mins, label: "Ministers Group" },
];

// Responsive hook
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 700);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

const Fellowships: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useIsMobile();

  const nextSlide = () => setCurrent((prev) => (prev + 1) % images.length);
  const prevSlide = () =>
    setCurrent((prev) => (prev - 1 + images.length) % images.length);

  useEffect(() => {
    timeoutRef.current = setTimeout(nextSlide, 5000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [current]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const leftIdx = (current - 1 + images.length) % images.length;
  const rightIdx = (current + 1) % images.length;

  return (
    <motion.section
      className="fellowships-carousel"
      variants={staggerContainer(0.2, 0.3)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: false, amount: 0.25 }}
    >
      <motion.div
        className="fellowships-header"
        variants={fadeIn("up", "spring", 0.1, 0.8)}
      >
        <div className="fellowships-title">GROUPS</div>
        <div className="fellowships-main-heading">
          Join a Family of Believers
        </div>
        <div className="fellowships-subtext">
          Meet fellow Believers, Be discipled. Grow in faith. Walk in power.
        </div>
      </motion.div>

      <motion.div
        className="fellowships-carousel-row"
        variants={fadeIn("up", "spring", 0.1, 1.3)}
      >
        {isMobile ? (
          <div
            className="carousel-img active"
            style={{
              backgroundImage: `url(${images[current].src})`,
              position: "relative",
            }}
            aria-label={`Go to slide ${current + 1}`}
          >
            <div className="carousel-img-label">{images[current].label}</div>
          </div>
        ) : (
          [leftIdx, current, rightIdx].map((idx, i) => {
            let className = "carousel-img";
            if (idx === current) className += " active";
            else if (idx === leftIdx) className += " left";
            else if (idx === rightIdx) className += " right";
            return (
              <div
                key={idx}
                className={className}
                style={{
                  backgroundImage: `url(${images[idx].src})`,
                  position: "relative",
                }}
                onClick={() => setCurrent(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              >
                <div className="carousel-img-label">{images[idx].label}</div>
              </div>
            );
          })
        )}
        <button
          className="carousel-arrow left-arrow"
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          &#8592;
        </button>
        <button
          className="carousel-arrow right-arrow"
          onClick={nextSlide}
          aria-label="Next slide"
        >
          &#8594;
        </button>
      </motion.div>
      <div className="carousel-dots">
        {images.map((_, idx) => (
          <span
            key={idx}
            className={`carousel-dot${idx === current ? " active" : ""}`}
            onClick={() => setCurrent(idx)}
          />
        ))}
      </div>
      <Button
        text="Join a group"
        onClick={() =>
          window.open(
            "https://launcher.nucleus.church/flow/47e0a0536ea78c712113?nucleuslauncher=openflow&nlflow=47e0a0536ea78c712113",
            "_blank",
            "noopener,noreferrer"
          )
        }
      />
    </motion.section>
  );
};

export default Fellowships;
